import { useRef, useState } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { Copy, Download, Globe, IdCard, QrCode, Share2, TriangleAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { Btn, Modal } from '../shared';
import { API } from '../../lib/api';
import { canShareFile, canvasBlob, downloadBlob, drawShareCard, shareBlob } from '../../lib/shareCard';

const LOGO = '/brand/sports-hub-connect-wordmark.png';

const fileSlug = name => (name || 'coach').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'coach';

const FullScreenQr = ({ url, name, onExit }) => {
  const start = useRef(0);
  return <div className="share-qr-fullscreen" role="dialog" aria-label="Full screen Passport QR code" data-testid="share-qr-fullscreen"
    onClick={onExit}
    onTouchStart={e => { start.current = e.touches[0].clientY; }}
    onTouchEnd={e => { if (e.changedTouches[0].clientY - start.current > 60) onExit(); }}>
    <QRCodeSVG value={url} size={320} level="M" bgColor="#000000" fgColor="#ffffff" data-testid="share-qr-fullscreen-code" />
    <p data-testid="share-qr-fullscreen-name">{name}</p>
    <span className="share-qr-exit-hint">Tap anywhere or swipe down to exit</span>
  </div>;
};

export const PassportShareSection = ({ passport, url, isPublic, onMakePublic, sport = 'Baseball' }) => {
  const qrRef = useRef(null);
  const [card, setCard] = useState(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [full, setFull] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const incomplete = isPublic && (!passport.photo_id || !passport.bio?.trim());
  const slug = fileSlug(passport.name);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success('Link copied'); }
    catch { toast.error('Copy the link from the QR window instead.'); }
  };

  const openCard = async () => {
    setBusy(true);
    setCardOpen(true);
    const build = async photoSrc => {
      const canvas = await drawShareCard({ passport, url, sport, qrCanvas: qrRef.current?.querySelector('canvas'), logoSrc: LOGO, photoSrc });
      return { src: canvas.toDataURL('image/png'), blob: await canvasBlob(canvas) };
    };
    const photoSrc = passport.photo_id ? `${API}/files/${passport.photo_id}` : null;
    try {
      setCard(await build(photoSrc));
    } catch {
      try { setCard(await build(null)); }
      catch {
        toast.error('The share card could not be generated. Try again.');
        setCardOpen(false);
      }
    } finally { setBusy(false); }
  };

  const qrBlob = async () => {
    const source = qrRef.current?.querySelector('canvas');
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 720;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, 720, 720);
    if (source) ctx.drawImage(source, 60, 60, 600, 600);
    return canvasBlob(canvas);
  };

  const shareImage = async (blob, filename) => {
    try {
      if (await shareBlob(blob, filename, `${passport.name} · Sports Hub Connect`)) return;
      downloadBlob(blob, filename);
      toast.success('Saved to your device — sharing isn’t available in this browser');
    } catch { /* the share sheet was dismissed */ }
  };

  return <section className="share-section" data-testid="passport-share-section">
    <p className="share-section-label" data-testid="passport-share-section-label">SHARE YOUR PASSPORT</p>
    {!isPublic
      ? <div className="share-section-private" data-testid="passport-share-private">
        <p>Make your Passport public to share.</p>
        <Btn secondary data-testid="passport-share-make-public" onClick={onMakePublic}><Globe />Make it public</Btn>
      </div>
      : <>
        {incomplete && !dismissed && <p className="share-section-warning" role="status" data-testid="passport-share-incomplete-warning">
          <TriangleAlert size={15} />Your Passport is missing a photo or bio — parents scanning this will see limited information.
          <button aria-label="Dismiss warning" data-testid="passport-share-warning-dismiss" onClick={() => setDismissed(true)}><X size={14} /></button>
        </p>}
        <div className="share-section-actions">
          <Btn secondary data-testid="passport-share-card-button" onClick={openCard}><IdCard />Share Card</Btn>
          <Btn secondary data-testid="passport-share-copy-button" onClick={copy}><Copy />Copy Link</Btn>
          <Btn secondary data-testid="passport-share-qr-button" onClick={() => setQrOpen(true)}><QrCode />QR Code</Btn>
        </div>
      </>}
    <div className="share-qr-source" ref={qrRef} aria-hidden="true"><QRCodeCanvas value={url} size={624} level="M" bgColor="#0a0a0a" fgColor="#ffffff" marginSize={2} /></div>

    <Modal className="passport-refresh tp-dialog" id="passport-share-card-modal" closeTestId="passport-share-card-close" open={cardOpen} onClose={() => setCardOpen(false)}
      title="Your coaching card" description="A square image built from your saved Passport — ready to text to a parent.">
      <div className="share-card-preview">
        {busy || !card ? <p className="form-hint" data-testid="passport-share-card-loading">Building your card…</p>
          : <>
            <img src={card.src} alt={`${passport.name} share card`} data-testid="passport-share-card-image" />
            <div className="share-card-actions">
              <Btn data-testid="passport-share-card-download" onClick={() => downloadBlob(card.blob, `${slug}-sports-hub-connect.png`)}><Download />Download</Btn>
              <Btn secondary data-testid="passport-share-card-share" onClick={() => shareImage(card.blob, `${slug}-sports-hub-connect.png`)}><Share2 />{canShareFile() ? 'Share' : 'Share / Save'}</Btn>
            </div>
          </>}
      </div>
    </Modal>

    <Modal className="passport-refresh tp-dialog" id="passport-share-qr-modal" closeTestId="passport-share-qr-close" open={qrOpen} onClose={() => setQrOpen(false)}
      title="Scan to connect" description="Show this to a parent in person or on screen.">
      <div className="share-qr-panel">
        <p className="share-qr-instruction" data-testid="passport-share-qr-instruction">Point your camera at this code to view {passport.name}’s coaching profile</p>
        <button className="share-qr-tap" data-testid="passport-share-qr-enlarge" aria-label="Show the code full screen" onClick={() => setFull(true)}>
          <QRCodeSVG value={url} size={224} level="M" bgColor="#0a0a0a" fgColor="#ffffff" data-testid="passport-share-qr-code" />
        </button>
        <strong data-testid="passport-share-qr-name">{passport.name}</strong>
        <p className="share-qr-title" data-testid="passport-share-qr-title">{passport.title}</p>
        <span className="form-hint">Tap the code for full screen scan mode</span>
        <div className="share-card-actions">
          <Btn data-testid="passport-share-qr-download" onClick={async () => downloadBlob(await qrBlob(), `${slug}-passport-qr.png`)}><Download />Save as image</Btn>
          <Btn secondary data-testid="passport-share-qr-share" onClick={async () => shareImage(await qrBlob(), `${slug}-passport-qr.png`)}><Share2 />{canShareFile() ? 'Share' : 'Share / Save'}</Btn>
        </div>
      </div>
    </Modal>
    {full && <FullScreenQr url={url} name={passport.name} onExit={() => setFull(false)} />}
  </section>;
};
